"use client";

import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { DropdownMenuField } from './dropdown-menu-field';
import { useFormModeGuard } from './use-form-mode-guard';

export interface FormOption {
  value: string;
  label: string;
}

const optionsSchema = z.array(z.object({ value: z.string(), label: z.string() }));

interface ApiDropdownFieldProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  loader: () => Promise<FormOption[]>;
  reloadKey?: string;
  onValueChange?: (value: string) => void;
}

export function ApiDropdownField({
  name,
  label,
  required = false,
  disabled = false,
  placeholder = '- Selecciona una opción -',
  loader,
  reloadKey,
  onValueChange,
}: ApiDropdownFieldProps) {
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const [options, setOptions] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const loaderRef = useRef(loader);

  useEffect(() => {
    loaderRef.current = loader;
  }, [loader]);

  useEffect(() => {
    if (!sideEffectsAllowed) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loaderRef.current();
        if (!mounted) return;
        const result = optionsSchema.safeParse(data);
        if (result.success) {
          setOptions(result.data);
        } else {
          setOptions([]);
          setError('La respuesta del catálogo no tiene un formato válido.');
        }
      } catch (e) {
        console.error('Error cargando opciones del API:', e);
        if (mounted) {
          setOptions([]);
          setError(e instanceof Error ? e.message : 'No se pudieron cargar las opciones.');
        }
      }
      if (!mounted) return;
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [reloadKey, retryCount, sideEffectsAllowed]);

  const effectivePlaceholder = loading ? 'Cargando...' : placeholder;

  return (
    <>
      <DropdownMenuField
        name={name}
        label={label}
        placeholder={effectivePlaceholder}
        options={options}
        required={required}
        disabled={!sideEffectsAllowed || loading}
        onValueChange={onValueChange}
      />
      {sideEffectsAllowed && error ? (
        <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => setRetryCount((current) => current + 1)}
          >
            Reintentar
          </Button>
        </div>
      ) : null}
    </>
  );
}
